import * as React from 'react';
import {alpha, Theme, Components} from '@mui/material/styles';
import {outlinedInputClasses} from '@mui/material/OutlinedInput';
import {svgIconClasses} from '@mui/material/SvgIcon';
import {toggleButtonGroupClasses} from '@mui/material/ToggleButtonGroup';
import {toggleButtonClasses} from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import {gray, brand} from '../themePrimitives';

export const inputsCustomizations: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({theme}) => ({
        boxSizing: 'border-box',
        transition: 'all 100ms ease-in',
        '&:focus-visible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({theme}) => ({
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        variants: [
          {
            props: {size: 'small'},
            style: {height: '2.25rem', padding: '8px 12px'},
          },
          {
            props: {size: 'medium'},
            style: {height: '2.5rem'},
          },
          {
            props: {color: 'primary', variant: 'contained'},
            style: {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {backgroundColor: theme.palette.primary.dark},
              '&:active': {backgroundColor: theme.palette.primary.dark},
            },
          },
          {
            props: {color: 'secondary', variant: 'contained'},
            style: {
              color: theme.palette.secondary.contrastText,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {backgroundColor: theme.palette.secondary.dark},
              '&:active': {backgroundColor: theme.palette.secondary.dark},
            },
          },
          {
            props: {variant: 'outlined'},
            style: {
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              backgroundColor: alpha(theme.palette.background.default, 0.3),
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.divider,
              },
              '&:active': {backgroundColor: theme.palette.action.selected},
            },
          },
          {
            props: {color: 'secondary', variant: 'outlined'},
            style: {
              color: theme.palette.secondary.main,
              borderColor: alpha(theme.palette.secondary.main, 0.5),
              backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                borderColor: theme.palette.secondary.main,
              },
              '&:active': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.16),
              },
            },
          },
          {
            props: {variant: 'text'},
            style: {
              color: theme.palette.text.secondary,
              '&:hover': {backgroundColor: theme.palette.action.hover},
              '&:active': {backgroundColor: theme.palette.action.selected},
            },
          },
          {
            props: {color: 'secondary', variant: 'text'},
            style: {
              color: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              },
              '&:active': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.16),
              },
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({theme}) => ({
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.primary,
        border: '1px solid',
        borderColor: theme.palette.divider,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          borderColor: theme.palette.divider,
        },
        '&:active': {backgroundColor: theme.palette.action.selected},
        variants: [
          {
            props: {size: 'small'},
            style: {
              width: '2.25rem',
              height: '2.25rem',
              padding: '0.25rem',
              [`& .${svgIconClasses.root}`]: {fontSize: '1rem'},
            },
          },
          {
            props: {size: 'medium'},
            style: {width: '2.5rem', height: '2.5rem'},
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({theme}) => ({
        borderRadius: '10px',
        boxShadow: `0 4px 16px ${alpha(theme.palette.divider, 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: theme.palette.primary.main,
        },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({theme}) => ({
        padding: '12px 16px',
        textTransform: 'none',
        borderRadius: '10px',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        [`&.${toggleButtonClasses.selected}`]: {
          color: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: <CheckBoxOutlineBlankRoundedIcon />,
      checkedIcon: <CheckRoundedIcon sx={{height: 14, width: 14}} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{height: 14, width: 14}} />,
    },
    styleOverrides: {
      root: ({theme}) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: '1px solid',
        borderColor: theme.palette.divider,
        backgroundColor: alpha(theme.palette.background.default, 0.4),
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {borderColor: theme.palette.primary.main},
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: '2px',
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-checked': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          boxShadow: 'none',
          '&:hover': {backgroundColor: theme.palette.primary.dark},
        },
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {border: 'none'},
      input: {
        '&::placeholder': {
          opacity: 0.7,
          color: (theme: Theme) => theme.palette.text.secondary,
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {padding: 0},
      root: ({theme}) => ({
        padding: '8px 12px',
        color: theme.palette.text.primary,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: 'border 120ms ease-in',
        '&:hover': {borderColor: theme.palette.divider},
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          borderColor: theme.palette.primary.main,
        },
        variants: [
          {
            props: {size: 'small'},
            style: {height: '2.25rem'},
          },
          {
            props: {size: 'medium'},
            style: {height: '2.5rem'},
          },
        ],
      }),
      notchedOutline: {border: 'none'},
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({theme}) => ({
        color: theme.palette.text.secondary,
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({theme}) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
      }),
    },
  },
};
